import { appState } from './state'
import { CurrentAppState } from '../Models/AppState'
import {
    undoButtonX,
    undoButtonY,
    menuButtonX,
    menuButtonY,
    newGameButtonX,
    newGameButtonY,
    recordsToggleButtonX,
    recordsToggleButtonY,
    autoCompleteButtonX,
    autoCompleteButtonY,
} from './allXYCoordinates'
import { isLightBackground } from './colorUtils'

// ─── Pressed state tracking ─────────────────────────────────────────────────

let _pressedButtonId: string | null = null

/** Mark a button as pressed. Call schedulePaint() after. */
export function setButtonPressedItem(id: string): void {
    _pressedButtonId = id
}

/** Clear the pressed state. Call schedulePaint() after. */
export function clearButtonPressedItem(): void {
    _pressedButtonId = null
}

// Helper function to get icon color based on background
function getIconColor(): string {
    const bgColor = appState.currentTheme.backgroundColor
    return isLightBackground(bgColor) ? '#000000' : '#FFFFFF'
}

// Helper function to draw custom icon buttons with consistent style
function drawIconButton(
    x: number,
    y: number,
    size: number,
    iconType: 'menu' | 'undo' | 'newGame' | 'check' | 'list',
    ctx: CanvasRenderingContext2D,
    buttonId: string
) {
    // Draw press highlight overlay if this button is pressed
    if (_pressedButtonId === buttonId) {
        const lightBg = isLightBackground(appState.currentTheme.backgroundColor)
        const overlayColor = lightBg ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.15)'
        const r = Math.floor(size * 0.22)
        ctx.save()
        ctx.fillStyle = overlayColor
        ctx.beginPath()
        ctx.roundRect(x, y, size, size, r)
        ctx.fill()
        ctx.restore()
    }

    ctx.save()

    // Get icon color based on background brightness
    const iconColor = getIconColor()

    // Thinner, more minimal stroke style
    ctx.fillStyle = iconColor
    ctx.strokeStyle = iconColor
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.lineWidth = Math.max(2, size * 0.05)

    const padding = size * 0.2
    const iconSize = size - padding * 2
    const centerX = x + size / 2
    const centerY = y + size / 2

    switch (iconType) {
        case 'menu': {
            const isMenuOpen = appState.currentAppState === CurrentAppState.Menu

            if (isMenuOpen) {
                // X icon — two diagonal lines crossing at center
                const armLen = iconSize * 0.28
                ctx.beginPath()
                ctx.moveTo(centerX - armLen, centerY - armLen)
                ctx.lineTo(centerX + armLen, centerY + armLen)
                ctx.stroke()

                ctx.beginPath()
                ctx.moveTo(centerX + armLen, centerY - armLen)
                ctx.lineTo(centerX - armLen, centerY + armLen)
                ctx.stroke()
            } else {
                // Three horizontal lines — hamburger menu
                const lineSpacing = iconSize * 0.28
                const lineWidth = iconSize * 0.55
                const startX = centerX - lineWidth / 2

                ctx.beginPath()
                ctx.moveTo(startX, centerY - lineSpacing)
                ctx.lineTo(startX + lineWidth, centerY - lineSpacing)
                ctx.stroke()

                ctx.beginPath()
                ctx.moveTo(startX, centerY)
                ctx.lineTo(startX + lineWidth, centerY)
                ctx.stroke()

                ctx.beginPath()
                ctx.moveTo(startX, centerY + lineSpacing)
                ctx.lineTo(startX + lineWidth, centerY + lineSpacing)
                ctx.stroke()
            }
            break
        }

        case 'undo': {
            // Reply-style undo arrow as stroked path (consistent weight with other icons).
            // Horizontal line going left → 90° curved corner → vertical line going down,
            // with a chevron arrowhead at the left tip.

            // Path dimensions within the standard icon area
            const topY = centerY - iconSize * 0.15
            const leftX = centerX - iconSize * 0.38
            const rightX = centerX + iconSize * 0.35
            const bottomY = centerY + iconSize * 0.38
            const cr = iconSize * 0.22 // corner radius

            // Main path: horizontal left → curve down → vertical
            ctx.beginPath()
            ctx.moveTo(leftX, topY)
            ctx.lineTo(rightX - cr, topY)
            ctx.arcTo(rightX, topY, rightX, topY + cr, cr)
            ctx.lineTo(rightX, bottomY)
            ctx.stroke()

            // Chevron arrowhead at left end, pointing left
            const aLen = iconSize * 0.22
            ctx.beginPath()
            ctx.moveTo(leftX + aLen, topY - aLen)
            ctx.lineTo(leftX, topY)
            ctx.lineTo(leftX + aLen, topY + aLen)
            ctx.stroke()

            break
        }

        case 'newGame': {
            // "+" sign — universally understood, reads perfectly at any size
            const armLen = iconSize * 0.28

            // Horizontal bar
            ctx.beginPath()
            ctx.moveTo(centerX - armLen, centerY)
            ctx.lineTo(centerX + armLen, centerY)
            ctx.stroke()

            // Vertical bar
            ctx.beginPath()
            ctx.moveTo(centerX, centerY - armLen)
            ctx.lineTo(centerX, centerY + armLen)
            ctx.stroke()

            break
        }

        case 'check': {
            // Checkmark icon for auto-complete
            const checkSize = iconSize * 0.6
            const startCheckX = centerX - checkSize * 0.4
            const startCheckY = centerY + checkSize * 0.05
            const midCheckX = centerX - checkSize * 0.1
            const midCheckY = centerY + checkSize * 0.3
            const endCheckX = centerX + checkSize * 0.4
            const endCheckY = centerY - checkSize * 0.25

            ctx.beginPath()
            ctx.moveTo(startCheckX, startCheckY)
            ctx.lineTo(midCheckX, midCheckY)
            ctx.lineTo(endCheckX, endCheckY)
            ctx.stroke()
            break
        }

        case 'list': {
            // Simplified podium icon for leaderboard (no tiny numbers, monoline style).
            const baseY = centerY + iconSize * 0.3
            const sideTopY = centerY - iconSize * 0.02
            const centerTopY = centerY - iconSize * 0.28

            const sideW = iconSize * 0.24
            const centerW = iconSize * 0.26

            const centerBlockX = centerX - centerW / 2
            const leftBlockX = centerBlockX - sideW * 0.96
            const rightBlockX = centerBlockX + centerW - sideW * 0.04
            const cornerR = iconSize * 0.07

            // Slightly thinner than other icons to keep the small shape airy.
            ctx.lineWidth = Math.max(1.6, size * 0.04)

            ctx.beginPath()
            ctx.roundRect(leftBlockX, sideTopY, sideW, baseY - sideTopY, cornerR)
            ctx.stroke()

            ctx.beginPath()
            ctx.roundRect(centerBlockX, centerTopY, centerW, baseY - centerTopY, cornerR)
            ctx.stroke()

            ctx.beginPath()
            ctx.roundRect(rightBlockX, sideTopY, sideW, baseY - sideTopY, cornerR)
            ctx.stroke()
            break
        }
    }

    ctx.restore()
}

export function drawUndoButton(ctx: CanvasRenderingContext2D) {
    const x = undoButtonX()
    const y = undoButtonY()
    drawIconButton(x, y, appState.cardWidth, 'undo', ctx, 'btn-undo')
}

export function drawMenuButton(ctx: CanvasRenderingContext2D) {
    const x = menuButtonX()
    const y = menuButtonY()
    drawIconButton(x, y, appState.cardWidth, 'menu', ctx, 'btn-menu')
}

export function drawNewGameButton(ctx: CanvasRenderingContext2D) {
    const x = newGameButtonX()
    const y = newGameButtonY()
    drawIconButton(x, y, appState.cardWidth, 'newGame', ctx, 'btn-newgame')
}

export function drawAutoCompleteButton(ctx: CanvasRenderingContext2D) {
    const x = autoCompleteButtonX()
    const y = autoCompleteButtonY()
    drawIconButton(x, y, appState.cardWidth, 'check', ctx, 'btn-autocomplete')
}

export function drawRecordsToggleButton(ctx: CanvasRenderingContext2D) {
    const x = recordsToggleButtonX()
    const y = recordsToggleButtonY()
    drawIconButton(x, y, appState.cardWidth, 'list', ctx, 'btn-records')
}
